import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { ClubFormModal } from '@/components/clubs'
import { Button, Card, CardContent } from '@/components/ui'
import { useCreateClub, useMyClubs } from '@/hooks/clubs'

export function ClubsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: clubs = [], isLoading } = useMyClubs()
  const createClub = useCreateClub()
  const [showCreate, setShowCreate] = useState(false)

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-foreground">{t('clubs.title')}</h1>
          <p className="mt-1 text-muted-foreground">{t('clubs.subtitle')}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="w-full sm:w-auto">
          {t('clubs.create')}
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : clubs.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <div className="mb-4 text-4xl">📚</div>
            <p className="mb-1 font-medium text-foreground">{t('clubs.empty.title')}</p>
            <p className="mb-6 text-sm text-muted-foreground">{t('clubs.empty.description')}</p>
            <Button onClick={() => setShowCreate(true)}>{t('clubs.create')}</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {clubs.map((club) => (
            <Link
              key={club.id}
              to={`/dashboard/clubs/${club.id}`}
              className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Card className="h-full border-0 shadow-sm transition-shadow hover:shadow-md min-w-0">
                <CardContent className="space-y-2 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h2 className="truncate text-lg font-semibold text-foreground">{club.name}</h2>
                    {club.my_role === 'admin' && (
                      <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        {t('clubs.roles.admin')}
                      </span>
                    )}
                  </div>
                  {club.description && (
                    <p className="line-clamp-2 text-sm text-muted-foreground">{club.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    👥 {t('clubs.memberCount', { count: club.member_count })}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <ClubFormModal
          title={t('clubs.create')}
          submitLabel={t('clubs.form.create')}
          isPending={createClub.isPending}
          error={createClub.isError}
          onClose={() => setShowCreate(false)}
          onSubmit={async (values) => {
            const clubId = await createClub.mutateAsync(values)
            setShowCreate(false)
            navigate(`/dashboard/clubs/${clubId}`)
          }}
        />
      )}
    </div>
  )
}
